/**
 * Fixture-to-fixture collision detection using SAT.
 * Pure computation — checks if two room objects overlap in the XZ plane.
 */

const {
  getObjectCorners,
  getObjectClearanceFootprints,
  doPolygonsOverlap,
} = require('./geometry');
const {
  getClearanceConfig,
  isExpectedOverlap,
  sharesKitchenClearance,
  needsStrictKitchenClearance,
} = require('./clearanceConfig');

// Cabinet has frontClearance: 0 in CLEARANCE_CONFIG but still owns per-sink
// clearance footprints — its check needs to run regardless of the numeric
// frontClearance value.
function _hasMeaningfulClearance(obj, config) {
  if ((obj.type || '').toLowerCase() === 'lower-cabinet') return true;
  return config.frontClearance > 0 || config.sideClearance > 0;
}

/**
 * Check if a fixture's physical bounds overlap with any other fixture in the room.
 *
 * @param {Object} object - The fixture being placed
 * @param {Object[]} allObjects - All objects in the room
 * @param {Object} [options]
 * @param {string[]} [options.excludeIds] - Object IDs to skip
 * @param {string[]} [options.excludeGroupIds] - fixtureGroupIds to skip
 * @returns {Array<{type, target, reason}>} Collisions found
 */
function checkFixtureCollisions(object, allObjects, options = {}) {
  const { excludeIds = [], excludeGroupIds = [] } = options;
  const collisions = [];

  if (!object.center || !object.dimensions) return collisions;

  const config = getClearanceConfig(object);
  if (config.skipCollisionCheck) return collisions;

  const objCorners = getObjectCorners(object);
  const objGroupId = object.fixtureGroupId;

  for (const other of allObjects) {
    if (other.id === object.id) continue;
    if (excludeIds.includes(other.id)) continue;
    if (other.fixtureGroupId && excludeGroupIds.includes(other.fixtureGroupId))
      continue;
    // Skip objects in the same fixture group (vanity parts don't collide with each other)
    if (objGroupId && other.fixtureGroupId === objGroupId) continue;
    // A stacked upper stands on the upper it names (`stackOf`): one is the
    // other's support, by construction (plan 2026-10-21, C21).
    if (object.stackOf === other.id || other.stackOf === object.id) continue;
    // …and the short cabinet stacked with a rack stands on or under the rack
    // it names (`rackOf`, `cabinet-runs/rackStacks.js`)
    if (object.rackOf === other.id || other.rackOf === object.id) continue;
    // …and a microwave stands in the cabinet it names (`microwaveCabinetId`,
    // `cabinet-runs/microwaveCabinets.js`): inside its opening, by
    // construction.
    if (
      object.microwaveCabinetId === other.id ||
      other.microwaveCabinetId === object.id
    )
      continue;
    if (!other.center || !other.dimensions) continue;

    const otherConfig = getClearanceConfig(other);
    if (otherConfig.skipCollisionCheck) continue;

    // Skip expected overlaps (e.g., sink on countertop)
    if (isExpectedOverlap(object.type, other.type)) continue;

    // Y-axis overlap check — skip if objects don't share vertical space
    const objHalfY = (object.dimensions.y || 0) / 2;
    const otherHalfY = (other.dimensions.y || 0) / 2;
    const yGap =
      Math.abs((object.center.y || 0) - (other.center.y || 0)) -
      objHalfY -
      otherHalfY;
    if (yGap > 0.01) continue; // 1cm tolerance

    const otherCorners = getObjectCorners(other);

    // 1. Physical vs physical overlap
    if (doPolygonsOverlap(objCorners, otherCorners)) {
      collisions.push({
        type: 'fixture-to-fixture',
        target: { id: other.id, type: other.type },
        reason: `Overlaps ${other.type || 'object'} ${other.id}`,
      });
      continue; // Physical overlap is worse than clearance — skip clearance checks for this pair
    }

    // Clearance-zone semantics:
    //   - Clearance vs clearance overlap is FINE — that's "sharing"
    //     the same aisle / standing space.
    //   - Body inside an APPLIANCE's clearance is a real collision
    //     (a cabinet in the fridge's swing zone blocks the door).
    //   - Body inside a CABINET's clearance is allowed when both
    //     objects are kitchen items (perpendicular cabinets at a
    //     corner are inside each other's 30" front-clearance —
    //     impossible to avoid given 24" depth + 30" clearance).
    const bothKitchen = sharesKitchenClearance(object.type, other.type);
    const skipObjectInOtherClearance =
      bothKitchen && !needsStrictKitchenClearance(other.type);
    const skipOtherInObjectClearance =
      bothKitchen && !needsStrictKitchenClearance(object.type);

    // 2. Object physical vs other's clearance zone
    if (
      !skipObjectInOtherClearance &&
      _hasMeaningfulClearance(other, otherConfig)
    ) {
      const otherFootprints = getObjectClearanceFootprints(
        other,
        otherConfig,
        allObjects
      );
      const hit = otherFootprints.some((fp) =>
        doPolygonsOverlap(objCorners, fp)
      );
      if (hit) {
        collisions.push({
          type: 'fixture-clearance',
          target: { id: other.id, type: other.type },
          reason: `Inside clearance zone of ${other.type || 'object'} ${other.id}`,
        });
      }
    }

    // 3. Object's clearance zone vs other's physical body
    if (
      !skipOtherInObjectClearance &&
      _hasMeaningfulClearance(object, config)
    ) {
      const objFootprints = getObjectClearanceFootprints(
        object,
        config,
        allObjects
      );
      const hit = objFootprints.some((fp) =>
        doPolygonsOverlap(fp, otherCorners)
      );
      if (hit) {
        collisions.push({
          type: 'clearance-blocked',
          target: { id: other.id, type: other.type },
          reason: `Clearance zone blocked by ${other.type || 'object'} ${other.id}`,
        });
      }
    }
  }

  return collisions;
}

// Accessories whose overlaps fix-all can resolve by regenerating a vanity's
// mirror + lights. Scoped to exactly this set so detection == fixability:
// we never surface an intra-group overlap the fix can't clear.
const VANITY_ACCESSORY_TYPES = new Set([
  'mirror',
  'wall-light',
  'overhead-wall-light',
  'ceiling-light',
]);

function isVanityAccessory(obj) {
  return VANITY_ACCESSORY_TYPES.has((obj.type || '').toLowerCase());
}

/**
 * Detect overlaps BETWEEN accessories within the same fixture group
 * (mirror-mirror, overhead-overhead, sconce-sconce, sconce-on-mirror).
 *
 * `checkFixtureCollisions` skips same-group pairs by design ("vanity parts
 * don't collide"), so these never surface there. This is the intra-group
 * counterpart, scoped to accessories — the exact set the fix-all flow can
 * resolve by regenerating the vanity's mirror + lights. Sink/faucet/cabinet
 * pairs are excluded (either expected overlaps, or not accessory-fixable).
 *
 * Uses the same OBB overlap + Y-range gate as the fixture scan, so accessories
 * at different heights (an overhead above its mirror) don't false-flag.
 *
 * @param {Object[]} objects - all room objects
 * @returns {Array<{type, groupId, a, b, reason}>}
 */
function checkIntraGroupAccessoryCollisions(objects = []) {
  const byGroup = new Map();
  for (const o of objects) {
    if (!o.fixtureGroupId || !o.center || !o.dimensions) continue;
    if (!isVanityAccessory(o)) continue;
    if (!byGroup.has(o.fixtureGroupId)) byGroup.set(o.fixtureGroupId, []);
    byGroup.get(o.fixtureGroupId).push(o);
  }

  const collisions = [];
  for (const [groupId, items] of byGroup) {
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i];
        const b = items[j];
        if (isExpectedOverlap(a.type, b.type)) continue;

        // Y-range gate — an overhead above its mirror shares no vertical span.
        const yGap =
          Math.abs((a.center.y || 0) - (b.center.y || 0)) -
          (a.dimensions.y || 0) / 2 -
          (b.dimensions.y || 0) / 2;
        if (yGap > 0.01) continue;

        if (doPolygonsOverlap(getObjectCorners(a), getObjectCorners(b))) {
          collisions.push({
            type: 'intra-vanity',
            groupId,
            a: { id: a.id, type: a.type },
            b: { id: b.id, type: b.type },
            reason: `${a.type || 'accessory'} overlaps ${b.type || 'accessory'} within the same vanity`,
          });
        }
      }
    }
  }
  return collisions;
}

module.exports = {
  checkFixtureCollisions,
  checkIntraGroupAccessoryCollisions,
};
